import React__default, { forwardRef, useRef, useEffect } from 'react';
import styled from 'styled-components';
import sx from '../../sx.js';
import { useRuleBuilderStepsContext, RuleBuilderStepsProvider } from './context/RuleBuilderStepsContext.js';
import { useRefObjectAsForwardedRef } from '../../hooks/useRefObjectAsForwardedRef.js';

// rely on the native semantics of the <li> element and handle keyboard navigation within the step's content.
const OrderedList = styled.ol.withConfig({
  displayName: "RuleBuilderSteps__OrderedList",
  componentId: "sc-zfepu6-0"
})(["margin:0;padding:0;text-indent:0;list-style-type:none;list-style:none;text-align:center;flex:1;", ""], sx);
/** A set of steps that describes the progression through or the steps in a workflow */
const RuleBuilderSteps = /*#__PURE__*/forwardRef(({
  children,
  ariaLabel,
  activeStep = 0,
  initialStep = 0,
  state,
  onSelectStep,
  sx: sxProp,
  testId,
  ...props
}, forwardedRef) => {
  // const {activeStep, nextStep, prevStep, setActiveStep, reset, isSelected, setIsSelected} = useRuleBuilderSteps({
  //   initialStep
  // })
  const childArr = React__default.Children.toArray(children);
  const containerRef = React__default.useRef(null);
  useRefObjectAsForwardedRef(forwardedRef, containerRef);
  const [stepCount, setStepCount] = React__default.useState(childArr.length);
  const [selectedStepIndex, setSelectedStepIndex] = React__default.useState(initialStep || 0);
  useRuleBuilderStepsContext();
  const stepRefs = useRef([]);

  // syncs stepRefs with correct number of steps
  useEffect(() => {
    stepRefs.current = stepRefs.current.slice(0, stepCount);
  }, [stepCount]);

  // keyboard navigation
  useEffect(() => {
    const handleKeyDown = event => {
      // Tab key
      if (event.key === 'Tab') {
        if (containerRef.current && containerRef.current.contains(document.activeElement)) {
          event.preventDefault();
          const activeIndex = stepRefs.current.findIndex(ref => ref === document.activeElement);
          const nextIndex = event.shiftKey ? activeIndex - 1 : activeIndex + 1;
          const numSteps = React__default.Children.count(children);
          if (activeIndex !== -1) {
            const isValidIndex = nextIndex >= 0 && nextIndex < numSteps;
            if (isValidIndex) {
              var _stepRefs$current$nex;
              if (onSelectStep) onSelectStep(nextIndex);
              (_stepRefs$current$nex = stepRefs.current[nextIndex]) === null || _stepRefs$current$nex === void 0 ? void 0 : _stepRefs$current$nex.focus();
            } else if (event.shiftKey && activeIndex === 0) {
              var _stepRefs$current;
              // Shift + Tab from first Step, focus on last Step
              if (onSelectStep) onSelectStep(numSteps - 1);
              (_stepRefs$current = stepRefs.current[numSteps - 1]) === null || _stepRefs$current === void 0 ? void 0 : _stepRefs$current.focus();
            } else if (!event.shiftKey && activeIndex === numSteps - 1) {
              const lastStep = stepRefs.current[numSteps - 1];
              if (lastStep instanceof HTMLElement) {
                lastStep.blur();
                const nextFocusableElement = containerRef.current.nextElementSibling;
                if (nextFocusableElement instanceof HTMLElement) {
                  nextFocusableElement.focus();
                }
              }
            }
          }
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedStepIndex, activeStep, onSelectStep, stepCount]);
  return /*#__PURE__*/React__default.createElement(RuleBuilderStepsProvider, {
    value: {
      activeStep,
      state,
      stepCount,
      onSelectStep
    }
  }, /*#__PURE__*/React__default.createElement(OrderedList, {
    "aria-label": ariaLabel,
    stepCount: stepCount,
    ref: containerRef,
    sx: sxProp
  }, React__default.Children.map(children, (child, i) => {
    const isLastStep = i === stepCount - 1;
    const isSecondToLastStep = i === stepCount - 2;
    const isSelectedStep = i === activeStep;
    const stepProps = {
      index: i,
      isLastStep,
      isSecondToLastStep,
      isSelectedStep,
      stepRef: element => {
        stepRefs.current[i] = element;
      }
    };
    if ( /*#__PURE__*/React__default.isValidElement(child)) {
      return /*#__PURE__*/React__default.cloneElement(child, stepProps);
    }
    return null;
  })));
});
RuleBuilderSteps.displayName = 'RuleBuilderSteps';
var RuleBuilderSteps$1 = RuleBuilderSteps;

export { RuleBuilderSteps$1 as default };
